import type { QuoteIncentiveRow } from './types'

type PriceBasedIncentiveType = QuoteIncentiveRow['settings']['price_based_incentive_type']

const PRICE_BASED_TYPES: ReadonlySet<string> = new Set([
  'price_including_tax',
  'price_excluding_tax',
  'price_payable_including_tax',
  'price_payable_excluding_tax',
])

export type IncentiveLibraryRecordLike = {
  settings?: unknown
  value?: unknown
  price_based_incentive_type?: unknown
}

export type ParsedIncentiveLibrarySettings = {
  value: number
  price_based_incentive_type: PriceBasedIncentiveType
}

function coerceFiniteNumber(raw: unknown): number | null {
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return raw
  }
  if (raw == null || raw === '') {
    return null
  }
  const parsed = parseFloat(String(raw))
  return Number.isFinite(parsed) ? parsed : null
}

function parseSettingsObject(raw: unknown): Record<string, unknown> {
  if (raw != null && typeof raw === 'object' && !Array.isArray(raw)) {
    return raw as Record<string, unknown>
  }
  if (typeof raw === 'string' && raw.trim()) {
    try {
      const parsed = JSON.parse(raw) as unknown
      if (parsed != null && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed as Record<string, unknown>
      }
    } catch {
      // ignore invalid JSON — same tolerance as incentives admin form
    }
  }
  return {}
}

function resolvePriceBasedType(raw: unknown): PriceBasedIncentiveType {
  if (typeof raw === 'string' && PRICE_BASED_TYPES.has(raw)) {
    return raw as PriceBasedIncentiveType
  }
  return 'price_including_tax'
}

/**
 * Read dollar amount or percent from an incentive library list record.
 *
 * List rows from {@link useGetListResourceData} may store `settings` as a JSON
 * string (react-admin round-trip) or flatten `value` onto the record root — the
 * Quote library picker must handle both so rows show the correct amount immediately.
 */
export function parseIncentiveLibrarySettings(record: IncentiveLibraryRecordLike): ParsedIncentiveLibrarySettings {
  const settingsObj = parseSettingsObject(record.settings)
  const value =
    coerceFiniteNumber(settingsObj.value) ??
    coerceFiniteNumber(record.value) ??
    0

  const price_based_incentive_type = resolvePriceBasedType(
    settingsObj.price_based_incentive_type ?? record.price_based_incentive_type
  )

  return { value, price_based_incentive_type }
}
